import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom'
import toast from 'react-hot-toast'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useTranslation } from '@/i18n/useTranslation'
import { authService } from '@/services/authService'
import { useAuthStore } from '@/stores/authStore'
import type { AuthOutletContext } from '@/layouts/AuthLayout'
import { TwoFactorLoginForm } from '@/components/auth/TwoFactorLoginForm'

const CustomerVerifyEmail = () => {
  const { t } = useTranslation('common')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { applySession } = useAuthStore()
  const { shopBranding } = useOutletContext<AuthOutletContext>()
  const { slug, shopQuery } = shopBranding

  const [status, setStatus] = useState<'loading' | 'ok' | 'error' | 'mfa'>('loading')
  const [message, setMessage] = useState('')
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [redirectPath, setRedirectPath] = useState<string | null>(null)

  useEffect(() => {
    const token = searchParams.get('token')
    if (!token) {
      setStatus('error')
      setMessage(t('auth.customerVerify.missingToken'))
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await authService.verifyCustomerEmail(token)
        if (cancelled) return
        if (!res.success) {
          setStatus('error')
          setMessage(res.message || t('auth.customerVerify.failed'))
          return
        }
        const data = res.data
        if (data?.pending_2fa && data.challenge_id) {
          setChallengeId(data.challenge_id)
          setRedirectPath(data.redirect_path || null)
          setStatus('mfa')
          return
        }
        if (data?.token && data.user) {
          applySession({
            token: data.token,
            user: { ...data.user, is_web_user: true },
            expires_in: data.expires_in,
            idle_timeout_minutes: data.idle_timeout_minutes,
            max_session_expires_at: data.max_session_expires_at,
          })
          toast.success(t('auth.customerVerify.success'))
          setStatus('ok')
          const target =
            data.redirect_path ||
            (slug ? `/shop/${slug}` : '/customer')
          setTimeout(() => navigate(target, { replace: true }), 400)
          return
        }
        setStatus('error')
        setMessage(t('auth.customerVerify.failed'))
      } catch (error: unknown) {
        if (cancelled) return
        const err = error as { response?: { data?: { message?: string } } }
        setStatus('error')
        setMessage(err?.response?.data?.message || t('auth.customerVerify.failed'))
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per token
  }, [searchParams])

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-gray-600">{t('auth.customerVerify.confirming')}</p>
      </div>
    )
  }

  if (status === 'mfa' && challengeId) {
    return (
      <TwoFactorLoginForm
        challengeId={challengeId}
        onSuccess={(session) => {
          applySession({
            token: session.token,
            user: { ...session.user, is_web_user: true },
            expires_in: session.expires_in,
            idle_timeout_minutes: session.idle_timeout_minutes,
            max_session_expires_at: session.max_session_expires_at,
          })
          toast.success(t('auth.customerVerify.success'))
          navigate(redirectPath || (slug ? `/shop/${slug}` : '/customer'), { replace: true })
        }}
      />
    )
  }

  if (status === 'error') {
    return (
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.customerVerify.errorTitle')}</h2>
        <p className="text-sm text-gray-600">{message}</p>
        <Link
          to={`/auth/customer/login${shopQuery}`}
          className="inline-flex font-medium text-primary-600 hover:text-primary-500"
        >
          {t('auth.customerVerify.backToLogin')}
        </Link>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
      <LoadingSpinner size="lg" />
      <p className="text-sm text-gray-600">{t('auth.customerVerify.redirecting')}</p>
    </div>
  )
}

export default CustomerVerifyEmail
